import { cloneData, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { Price } from '../price.js';
import { CartItemDto } from './cart-item.js';
import { Product } from '../product.js';
import { VersionRange } from '../../core/version-range.js';

export class ProductCartItemDto extends CartItemDto {
  static readonly TYPE_ID: string = '2e56888e5c2e4784a1fc221cc309b1de';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly originalUnitPrice: Price | null;
  public readonly unitPrice: Price;

  private _totalPrice: Price;

  private constructor(
    public readonly eventId: string | null,
    public readonly quantity: number,
    public readonly product: Product,
  ) {
    super();
    this.unitPrice = product.price.clone();
    this.originalUnitPrice = product.originalPrice?.clone() ?? null;

    this._totalPrice = this.unitPrice.scale(quantity);
  }

  get $typeId(): string {
    return ProductCartItemDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(ProductCartItemDto.SCHEMA_VERSION);
  }

  get uniqueId(): string {
    return this.product.uniqueId;
  }

  get totalPrice(): Price {
    return this._totalPrice;
  }

  static create(args: {
    eventId: string | null,
    quantity: number,
    product: Product,
  }) {
    return new ProductCartItemDto(
        args.eventId,
        args.quantity,
        args.product,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProductCartItemDto {
    return new ProductCartItemDto(
        MapUtils.getStringOrNull(map, 'eventId'),
        MapUtils.getInteger(map, 'quantity'),
        context.fromData(MapUtils.getRecord(map, 'product')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'quantity': this.quantity,
      'product': context.toData(this.product),
    };
  }

  clone(): this {
    return new ProductCartItemDto(
        this.eventId,
        this.quantity,
        cloneData(this.product),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.product, this.product) &&
      other.eventId === this.eventId &&
      other.quantity === this.quantity;
  }

  static verifyType(item: CartItemDto): boolean {
    return item instanceof ProductCartItemDto;
  }

  static assertType(item: CartItemDto): item is ProductCartItemDto {
    return item instanceof ProductCartItemDto;
  }
}

export class ProductCartItemDtoBuilder extends DataModelBuilder<ProductCartItemDto> {
  build(context: SerializationContext, map: SerializedData): ProductCartItemDto {
    return ProductCartItemDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ProductCartItemDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ProductCartItemDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     * - add `eventId` of type `string | null`.
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
